import type { CSSProperties } from "react";
import { copy } from "../copy";
import { formatBps } from "../format";
import { pickKey, type LandingLiveContest, type Pick } from "../feed";
import type { BeatPlayer } from "../audio";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";
import { useTilt } from "../hooks/useTilt";
import BeatBars from "./BeatBars";

const MAX_CARDS = 3;

// One card per producer line across the live contests, most-backed first.
// A contest with no outcomes contributes nothing rather than an empty card.
function picksFrom(contests: LandingLiveContest[]): Pick[] {
  const now = Date.now();
  return contests
    .filter((c) => new Date(c.ends_at).getTime() > now)
    .flatMap((contest) => contest.outcomes.map((outcome) => ({ contest, outcome })))
    .sort((a, b) => b.outcome.backer_count - a.outcome.backer_count)
    .slice(0, MAX_CARDS);
}

type CardProps = {
  pick: Pick;
  player: BeatPlayer;
  onBack: (pick: Pick) => void;
  variant: "hero" | "line";
  // False hides Back: the viewer may see the odds but not pick.
  canBack?: boolean;
};

// A pick card: the template's PickCard in its two sizes. Only the hero
// variant carries the pick-card and play-preview test ids; the contest lines
// are a second view of the same outcomes.
export function PickCard({ pick, player, onBack, variant, canBack = true }: CardProps) {
  const reduced = usePrefersReducedMotion();
  const { cardRef, glareRef } = useTilt<HTMLElement>(!reduced, variant === "hero" ? 13 : 7);
  const { contest, outcome } = pick;
  const key = pickKey(pick);
  const playing = player.playingKey === key;
  const url = outcome.track_preview_url;
  const hero = variant === "hero";
  const favourite = outcome.american_odds.trim().startsWith("-");

  const playButton = url ? (
    <button
      type="button"
      data-testid={hero ? "play-preview" : "line-play"}
      aria-pressed={playing}
      aria-label={playing ? copy.pick.pause(outcome.username) : copy.pick.play(outcome.username)}
      onClick={() => player.toggle(key, url)}
      className="bf-focusable flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-money text-ground transition-colors hover:bg-money-deep"
    >
      {playing ? <PauseIcon /> : <PlayIcon />}
    </button>
  ) : null;

  const odds = (
    <span className={`bf-tabular font-headline leading-none ${favourite ? "text-chrome" : "text-gold"}`}>
      {outcome.american_odds}
    </span>
  );

  return (
    <article
      ref={cardRef}
      data-testid={hero ? "pick-card" : "pick-line"}
      className={`group relative overflow-hidden rounded-2xl border bg-ground-raised transition-colors ${
        playing ? "border-money" : "border-ground-edge hover:border-chrome-dim/40"
      } ${hero ? "w-full max-w-[300px] p-4 shadow-gold lg:w-[248px]" : "p-3"}`}
    >
      {/* Foil glare: drifts opposite the tilt, opacity follows its size. */}
      <div
        ref={glareRef}
        aria-hidden
        className="pointer-events-none absolute -inset-10 opacity-0"
        style={{ background: "linear-gradient(115deg, transparent 34%, rgba(255,255,255,0.16) 50%, transparent 66%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent"
      />

      {hero ? (
        <div className="relative">
          <div className="flex items-center gap-3">
            <Avatar username={outcome.username} logoUrl={outcome.logo_url} size="h-12 w-12" />
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-chrome">@{outcome.username}</p>
              <p className="truncate text-[11px] text-chrome-dim">{contest.name}</p>
            </div>
          </div>
          <div className="mt-4 flex items-end justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-chrome-dim">{copy.pick.odds}</p>
              <p className="text-3xl">{odds}</p>
            </div>
            <div className="text-right">
              <p className="bf-tabular text-[11px] text-chrome-dim">{copy.pick.ofPool(formatBps(outcome.share_bps))}</p>
              <p className="bf-tabular text-[11px] text-chrome-dim">{copy.pick.backers(outcome.backer_count)}</p>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 rounded-lg border border-ground-edge bg-ground p-2">
            {playButton}
            <BeatBars active={playing} read={player.readLevels} />
          </div>
          {canBack && (
            <button
              type="button"
              onClick={() => onBack(pick)}
              className="bf-focusable mt-3 h-10 w-full rounded-md bg-money text-xs font-bold uppercase tracking-[0.14em] text-ground transition-colors hover:bg-money-deep"
            >
              {copy.pick.backProducer(outcome.username)}
            </button>
          )}
        </div>
      ) : (
        <div className="relative flex items-center gap-3">
          <Avatar username={outcome.username} logoUrl={outcome.logo_url} size="h-10 w-10" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-chrome">@{outcome.username}</p>
            <div className="mt-1.5 flex items-center gap-2">
              {playButton}
              <BeatBars active={playing} read={player.readLevels} />
            </div>
          </div>
          <div className="shrink-0 text-right">
            <p className="text-xl">{odds}</p>
            <p className="bf-tabular text-[10px] text-chrome-dim">
              {formatBps(outcome.share_bps)} · {copy.pick.backers(outcome.backer_count)}
            </p>
          </div>
          {canBack && (
            <button
              type="button"
              onClick={() => onBack(pick)}
              aria-label={copy.pick.backProducer(outcome.username)}
              className="bf-focusable h-9 shrink-0 rounded-md bg-money px-4 text-[11px] font-bold uppercase tracking-[0.12em] text-ground transition-colors hover:bg-money-deep"
            >
              {copy.pick.back}
            </button>
          )}
        </div>
      )}
    </article>
  );
}

type Props = {
  contests: LandingLiveContest[];
  player: BeatPlayer;
  onBack: (pick: Pick) => void;
  // Docked: the bet slip holds the right edge on desktop, so the cards
  // stack in their column instead of fanning out under it.
  docked: boolean;
};

// The hero's hand. Phones and tablets stack the cards in one column; from lg
// they fan out with the most-backed pick in front.
export default function PickCards({ contests, player, onBack, docked }: Props) {
  const picks = picksFrom(contests);
  if (picks.length === 0) return null;
  const hasAudio = picks.some((p) => p.outcome.track_preview_url);

  // From lg the cards fan out with the most-backed in front and centre.
  // Each card's fan position rides on custom properties, so one list serves
  // both layouts and a phone just ignores them.
  const fanOffsets = picks.length === 3 ? [0, -1, 1] : picks.length === 2 ? [-0.5, 0.5] : [0];
  const fanVars = (i: number) => {
    const offset = fanOffsets[i];
    return {
      "--fan-x": `${offset * 158}px`,
      "--fan-y": `${Math.abs(offset) * 26}px`,
      "--fan-r": `${offset * 8}deg`,
      "--fan-z": String(offset === 0 ? 30 : 20 - i),
    } as CSSProperties;
  };

  return (
    <section id="picks" aria-label={copy.hero.picksHeading}>
      <div className="mb-4 flex items-center justify-center gap-3">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.24em] text-chrome-dim">{copy.hero.picksHeading}</h2>
        {hasAudio && (
          <button
            type="button"
            data-testid="mute-toggle"
            aria-pressed={player.muted}
            aria-label={player.muted ? copy.pick.unmute : copy.pick.mute}
            onClick={player.toggleMute}
            className="bf-focusable rounded-full border border-ground-edge p-2 text-chrome-dim transition hover:text-chrome"
          >
            <SpeakerIcon muted={player.muted} />
          </button>
        )}
      </div>

      <div
        className={`flex flex-col items-center gap-4 ${docked ? "" : "lg:relative lg:h-[360px] lg:flex-row lg:justify-center"}`}
      >
        {picks.map((pick, i) => (
          <div
            key={pickKey(pick)}
            style={docked ? undefined : fanVars(i)}
            className={`flex w-full justify-center lg:w-auto ${docked ? "" : "fan-slot"}`}
          >
            <PickCard pick={pick} player={player} onBack={onBack} variant="hero" />
          </div>
        ))}
      </div>
    </section>
  );
}

function Avatar({ username, logoUrl, size }: { username: string; logoUrl?: string; size: string }) {
  return (
    <span className={`${size} shrink-0 overflow-hidden rounded-full border border-ground-edge`}>
      {logoUrl ? (
        <img src={logoUrl} alt="" className="h-full w-full object-cover object-top" />
      ) : (
        <span className="flex h-full w-full items-center justify-center bg-ground-edge font-headline uppercase text-gold">
          {username.slice(0, 1)}
        </span>
      )}
    </span>
  );
}

function PlayIcon() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="ml-0.5 h-4 w-4 fill-current">
      <path d="M7 4.5v15l13-7.5z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="h-4 w-4 fill-current">
      <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
    </svg>
  );
}

function SpeakerIcon({ muted }: { muted: boolean }) {
  return (
    <svg aria-hidden viewBox="0 0 24 24" className="h-4 w-4 fill-none stroke-current" strokeWidth={2}>
      <path d="M4 9v6h4l5 4V5L8 9z" className="fill-current" />
      {muted ? <path d="M17 9l5 6M22 9l-5 6" /> : <path d="M17 8.5a5 5 0 0 1 0 7M19.5 6a8.5 8.5 0 0 1 0 12" />}
    </svg>
  );
}
